/**
 * Blackhole - 黑洞物理计算模块
 * @description 实现史瓦西黑洞光线追踪算法，计算光线在黑洞引力场中的弯曲路径
 */

import { Vec2 } from 'planck';

/**
 * 万有引力常数（游戏简化单位）
 * 用于计算史瓦西半径和引力效应
 * Rs = 2G*M/c²，简化为 Rs = 0.1 * mass（游戏单位）
 */
export const GRAVITATIONAL_CONSTANT = 0.05;

/**
 * 默认黑洞质量（游戏单位）
 */
export const DEFAULT_BLACKHOLE_MASS = 1000;

/**
 * 光线追踪步长（游戏单位）
 */
export const LIGHT_RAY_STEP_SIZE = 5;

/**
 * 光线追踪最大步数
 */
export const LIGHT_RAY_MAX_STEPS = 500;

/**
 * 最小距离阈值，防止数值问题
 */
export const MIN_DISTANCE_THRESHOLD = 1.0;

/**
 * 黑洞配置
 */
export interface BlackholeConfig {
  /** 黑洞唯一标识 */
  id: string;
  /** 黑洞位置（游戏坐标） */
  position: Vec2;
  /** 黑洞质量（游戏单位） */
  mass: number;
  /** 可选：旋转方向（影响光线偏转方向） */
  spin?: number;
}

/**
 * 光线追踪结果
 */
export interface LightRayTraceResult {
  /** 光线路径点数组（游戏坐标） */
  path: Vec2[];
  /** 是否被黑洞捕获（进入事件视界） */
  captured: boolean;
  /** 是否逃逸（距离足够远） */
  escaped: boolean;
  /** 最终偏折角（弧度） */
  deflectionAngle: number;
  /** 路径总长度（游戏单位） */
  pathLength: number;
  /** 最接近黑洞的距离（游戏单位） */
  closestDistance: number;
}

/**
 * 引力红移结果
 */
export interface GravitationalRedshiftResult {
  /** 红移值 z */
  redshift: number;
  /** 频率比率 f_observed/f_emitted = 1/(1+z) */
  frequencyRatio: number;
  /** 波长比率 λ_observed/λ_emitted = 1+z */
  wavelengthRatio: number;
  /** 观测者视角描述 */
  description: string;
}

/**
 * 黑洞引力势结果
 */
export interface GravitationalPotentialResult {
  /** 引力势能（游戏单位） */
  potential: number;
  /** 逃逸速度（游戏单位） */
  escapeVelocity: number;
  /** 与光速的比率 */
  lightSpeedRatio: number;
}

/**
 * Blackhole 类
 * 提供史瓦西黑洞的核心计算功能
 */
export class Blackhole {
  private config: BlackholeConfig;
  private schwarzschildRadius: number;

  constructor(config: BlackholeConfig) {
    this.config = config;
    this.schwarzschildRadius = this.calculateSchwarzschildRadius(config.mass);
  }

  getConfig(): BlackholeConfig {
    return this.config;
  }

  getPosition(): Vec2 {
    return this.config.position;
  }

  getMass(): number {
    return this.config.mass;
  }

  getSchwarzschildRadius(): number {
    return this.schwarzschildRadius;
  }

  /**
   * 计算史瓦西半径
   * 公式: Rs = 2GM/c²
   * 游戏简化: Rs = 2 * G * mass（c=1）
   */
  calculateSchwarzschildRadius(mass: number): number {
    return 2 * GRAVITATIONAL_CONSTANT * mass;
  }

  calculateDistance(position: Vec2): number {
    const dx = position.x - this.config.position.x;
    const dy = position.y - this.config.position.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  isInsideEventHorizon(position: Vec2): boolean {
    const distance = this.calculateDistance(position);
    return distance < this.schwarzschildRadius;
  }

  isNearEventHorizon(position: Vec2, threshold: number = 1.5): boolean {
    const distance = this.calculateDistance(position);
    return distance < this.schwarzschildRadius * threshold;
  }

  /**
   * 计算光线偏折角（弱场近似）
   * 公式: δ ≈ 4GM/(rc²) = 2Rs/r
   */
  calculateDeflectionAngle(position: Vec2): number {
    const distance = Math.max(this.calculateDistance(position), MIN_DISTANCE_THRESHOLD);
    
    if (distance <= this.schwarzschildRadius) {
      return Infinity;
    }

    return 2 * this.schwarzschildRadius / distance;
  }

  /**
   * 计算光线偏折方向向量
   */
  calculateDeflectionDirection(position: Vec2): Vec2 {
    const toBlackhole = Vec2(
      this.config.position.x - position.x,
      this.config.position.y - position.y
    );
    
    const distance = Math.sqrt(toBlackhole.x * toBlackhole.x + toBlackhole.y * toBlackhole.y);
    
    if (distance < MIN_DISTANCE_THRESHOLD) {
      return Vec2(0, 0);
    }

    const unitToBlackhole = Vec2(toBlackhole.x / distance, toBlackhole.y / distance);
    
    const deflectionStrength = this.schwarzschildRadius / (distance * distance);
    
    return Vec2(
      unitToBlackhole.x * deflectionStrength,
      unitToBlackhole.y * deflectionStrength
    );
  }

  /**
   * 追踪光线路径
   */
  traceLightRay(
    position: Vec2,
    direction: Vec2,
    options?: {
      stepSize?: number;
      maxSteps?: number;
      escapeDistance?: number;
    }
  ): LightRayTraceResult {
    const stepSize = options?.stepSize ?? LIGHT_RAY_STEP_SIZE;
    const maxSteps = options?.maxSteps ?? LIGHT_RAY_MAX_STEPS;
    const escapeDistance = options?.escapeDistance ?? this.schwarzschildRadius * 50;

    const path: Vec2[] = [Vec2(position.x, position.y)];
    let currentPos = Vec2(position.x, position.y);
    
    let dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    let currentDir = dirLength > 0 
      ? Vec2(direction.x / dirLength, direction.y / dirLength) 
      : Vec2(1, 0);

    let captured = false;
    let escaped = false;
    let totalLength = 0;
    let closestDistance = this.calculateDistance(currentPos);
    let initialAngle = Math.atan2(currentDir.y, currentDir.x);

    for (let step = 0; step < maxSteps; step++) {
      const distance = this.calculateDistance(currentPos);
      
      if (distance < closestDistance) {
        closestDistance = distance;
      }

      if (distance <= this.schwarzschildRadius) {
        captured = true;
        path.push(Vec2(this.config.position.x, this.config.position.y));
        break;
      }

      if (distance >= escapeDistance) {
        escaped = true;
        break;
      }

      const deflection = this.calculateDeflectionDirection(currentPos);
      
      const newDirX = currentDir.x + deflection.x * stepSize;
      const newDirY = currentDir.y + deflection.y * stepSize;
      
      const newDirLength = Math.sqrt(newDirX * newDirX + newDirY * newDirY);
      if (newDirLength > 0) {
        currentDir = Vec2(newDirX / newDirLength, newDirY / newDirLength);
      }

      currentPos = Vec2(
        currentPos.x + currentDir.x * stepSize,
        currentPos.y + currentDir.y * stepSize
      );
      
      path.push(Vec2(currentPos.x, currentPos.y));
      totalLength += stepSize;
    }

    let finalAngle = Math.atan2(currentDir.y, currentDir.x);
    let deflectionAngle = finalAngle - initialAngle;
    
    while (deflectionAngle > Math.PI) deflectionAngle -= 2 * Math.PI;
    while (deflectionAngle < -Math.PI) deflectionAngle += 2 * Math.PI;

    return {
      path,
      captured,
      escaped,
      deflectionAngle: captured ? Infinity : deflectionAngle,
      pathLength: totalLength,
      closestDistance,
    };
  }

  /**
   * 计算引力红移
   * 公式: z = 1/√(1 - Rs/r) - 1
   */
  calculateGravitationalRedshift(position: Vec2): GravitationalRedshiftResult {
    const rawDistance = this.calculateDistance(position);
    
    if (rawDistance <= this.schwarzschildRadius) {
      return {
        redshift: Infinity,
        frequencyRatio: 0,
        wavelengthRatio: Infinity,
        description: '位于事件视界内，光线无法逃逸',
      };
    }
    
    const distance = Math.max(rawDistance, MIN_DISTANCE_THRESHOLD);

    const ratio = Math.min(this.schwarzschildRadius / distance, 0.999);
    const sqrtFactor = Math.sqrt(1 - ratio);
    const redshift = (1 / sqrtFactor) - 1;
    
    const frequencyRatio = sqrtFactor;
    const wavelengthRatio = 1 / sqrtFactor;

    let description: string;
    if (redshift < 0.1) {
      description = `轻微引力红移 (z=${redshift.toFixed(3)})`;
    } else if (redshift < 1) {
      description = `中等引力红移 (z=${redshift.toFixed(2)})`;
    } else if (redshift < 10) {
      description = `强烈引力红移 (z=${redshift.toFixed(1)})`;
    } else {
      description = `极端引力红移`;
    }

    return {
      redshift,
      frequencyRatio,
      wavelengthRatio,
      description,
    };
  }

  /**
   * 计算引力势能
   */
  calculateGravitationalPotential(position: Vec2): GravitationalPotentialResult {
    const distance = Math.max(this.calculateDistance(position), MIN_DISTANCE_THRESHOLD);
    
    const potential = -GRAVITATIONAL_CONSTANT * this.config.mass / distance;
    const escapeVelocity = Math.sqrt(2 * Math.abs(potential));
    const lightSpeedRatio = escapeVelocity;

    return {
      potential,
      escapeVelocity,
      lightSpeedRatio,
    };
  }

  /**
   * 计算引力加速度
   */
  calculateGravitationalAcceleration(position: Vec2): Vec2 {
    const dx = this.config.position.x - position.x;
    const dy = this.config.position.y - position.y;
    const distanceSquared = Math.max(dx * dx + dy * dy, MIN_DISTANCE_THRESHOLD * MIN_DISTANCE_THRESHOLD);
    const distance = Math.sqrt(distanceSquared);

    const accelerationMagnitude = GRAVITATIONAL_CONSTANT * this.config.mass / distanceSquared;
    
    const unitX = dx / distance;
    const unitY = dy / distance;

    return Vec2(
      unitX * accelerationMagnitude,
      unitY * accelerationMagnitude
    );
  }

  /**
   * 批量追踪多条光线
   */
  batchTraceLightRays(
    startPosition: Vec2,
    rayCount: number,
    angleRange?: number
  ): LightRayTraceResult[] {
    const range = angleRange ?? Math.PI;
    const startAngle = Math.atan2(
      startPosition.y - this.config.position.y,
      startPosition.x - this.config.position.x
    );
    
    const results: LightRayTraceResult[] = [];
    const angleStep = range / Math.max(rayCount - 1, 1);

    for (let i = 0; i < rayCount; i++) {
      const angle = startAngle - range / 2 + i * angleStep;
      const direction = Vec2(Math.cos(angle), Math.sin(angle));
      results.push(this.traceLightRay(startPosition, direction));
    }

    return results;
  }

  getVisualizationPath(result: LightRayTraceResult): { x: number; y: number }[] {
    return result.path.map(point => ({ x: point.x, y: point.y }));
  }

  setPosition(position: Vec2): void {
    this.config.position = Vec2(position.x, position.y);
  }

  setMass(mass: number): void {
    this.config.mass = mass;
    this.schwarzschildRadius = this.calculateSchwarzschildRadius(mass);
  }

  getInfo(): {
    mass: number;
    schwarzschildRadius: number;
    position: { x: number; y: number };
    eventHorizonRadius: number;
  } {
    return {
      mass: this.config.mass,
      schwarzschildRadius: this.schwarzschildRadius,
      position: { x: this.config.position.x, y: this.config.position.y },
      eventHorizonRadius: this.schwarzschildRadius,
    };
  }
}

export function calculateSchwarzschildRadius(mass: number): number {
  return 2 * GRAVITATIONAL_CONSTANT * mass;
}

export function isInsideEventHorizon(
  position: Vec2,
  blackholeMass: number,
  blackholePosition: Vec2 = Vec2(0, 0)
): boolean {
  const rs = calculateSchwarzschildRadius(blackholeMass);
  const dx = position.x - blackholePosition.x;
  const dy = position.y - blackholePosition.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance < rs;
}

export function calculateDeflectionAngle(
  position: Vec2,
  blackholeMass: number,
  blackholePosition: Vec2 = Vec2(0, 0)
): number {
  const dx = position.x - blackholePosition.x;
  const dy = position.y - blackholePosition.y;
  const distance = Math.max(Math.sqrt(dx * dx + dy * dy), MIN_DISTANCE_THRESHOLD);
  const rs = calculateSchwarzschildRadius(blackholeMass);
  
  if (distance <= rs) {
    return Infinity;
  }

  return 2 * rs / distance;
}

export function calculateGravitationalRedshift(
  position: Vec2,
  blackholeMass: number,
  blackholePosition: Vec2 = Vec2(0, 0)
): number {
  const dx = position.x - blackholePosition.x;
  const dy = position.y - blackholePosition.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const rs = calculateSchwarzschildRadius(blackholeMass);
  
  if (distance <= rs) {
    return Infinity;
  }

  const ratio = Math.min(rs / distance, 0.999);
  return 1 / Math.sqrt(1 - ratio) - 1;
}

export function traceLightRay(
  position: Vec2,
  direction: Vec2,
  blackholeMass: number,
  blackholePosition: Vec2 = Vec2(0, 0),
  options?: {
    stepSize?: number;
    maxSteps?: number;
    escapeDistance?: number;
  }
): LightRayTraceResult {
  const blackhole = new Blackhole({
    id: 'temp',
    position: blackholePosition,
    mass: blackholeMass,
  });
  return blackhole.traceLightRay(position, direction, options);
}